import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { toastGetError, Toastify } from "../../../components/Toaster";
import { loadingSideEffects, updatingSideEffects, successSideEffects, errorSideEffects, addingSideEffects, deletingSideEffects, contentUpdatingSideEffects } from "..";
import { getOutcomeTrackingState, getRequestTrackingState, MutationTrackingState } from "../../../services/analytics/tracking";

// --- Types ---
export interface BrandStyle {
  IsDefault: boolean;
  Name: string;
  Config: {
    Light: V3Style;
    Dark: V3Style;
  };
}

export interface V3Style {
  common: CommonStyle;
  card: CardStyle;
}

export interface CommonStyle {
  favIcon: string;
  showLogo: boolean;
  logoUrl: string;
  brandColor: string;
  background: string;
  primaryTextColor: string;
  secondaryTextColor: string;
  fontFamily: string;
  textAlignment: string; // Consider "left" | "center" | "right" for stricter typing
}

export interface CardStyle {
  backgroundColor: string;
  width: number;
  padding: PaddingStyle;
  border: BorderStyle;
  boxShadow: string; // "none" | "soft" | "medium" | "hard" | any custom
  button: ButtonStyle;
  footer: FooterStyle;
  input: InputStyle;
  social: SocialStyle;
  header: TypographyFont;
  subHeader: TypographyFont;
}

export interface PaddingStyle {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface BorderStyle {
  width: BorderWidth;
  radius: BorderRadius;
  color: string;
}

export interface BorderWidth {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface BorderRadius {
  topLeft: number;
  topRight: number;
  bottomLeft: number;
  bottomRight: number;
}

export interface ButtonStyle {
  primaryButton: Button;
  secondaryButton: Button;
  link: Link;
}

export interface Button {
  borderColor: string;
  boxShadow: string;
  color: string;
  textColor: string;
  borderRadius: number;
}

export interface Link {
  textColor: string;
  fontWeight: number;
}

export interface FooterStyle {
  textColor: string;
  borderTop: string;
  backgroundColor: string;
}

export interface InputStyle {
  backgroundColor: string;
  textColor: string;
  borderColor: string;
}

export interface SocialStyle {
  backgroundColor: string;
  showIconOnly: boolean;
  borderRadius: number;
  boxShadow: string;
  layout: string; // Consider "top" | "bottom"
  borderColor: string;
  textColor: string;
}

export interface TypographyFont {
  fontSize: number;
  fontWeight: number;
}

export interface BrandI18n {
  Locale: string;
  Translations: Record<string, any>;
}

export interface Brand {
  id: string;
  BrandName: string;
  AppId: number;
  IsDefault: boolean;
  StyleMode: "light" | "dark" | "system";
  Styles: BrandStyle[];
  I18n: BrandI18n[];
  CreatedDate: string;
  ModifiedDate: string;
  Domains?: string[];
}

export interface BrandsState {
  Data: Brand[];
  isLoading: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isAdding: boolean;
  isSuccess: boolean;
  isError: boolean;
  errorMessage: string | null;
  errorCode: number | null;
}

export interface CurrentBrandState {
  Data: Brand | null;
  isLoading: boolean;
  isDefaultLoading: boolean;
  isContentUpdating: boolean;
  isUpdating: boolean;
  isSuccess: boolean;
  isError: boolean;
  errorMessage: string | null;

  // Styles
  isAdding: boolean;
  isDeleting: boolean;
}

export interface AddBrandPayload {
  BrandName: string;
  StyleMode?: "light" | "dark" | "system";
  Styles?: {
    Name: string;
    Config: {
      Light: V3Style;
      Dark: V3Style;
    };
  };
  I18n?: BrandI18n[];
}

export interface UpdateBrandPayload extends AddBrandPayload {
  id: string;
}

export interface AddStylePayload {
  brandId: string;
  style: {
    Name: string;
    Config?: {
      Light: V3Style;
      Dark: V3Style;
    };
  };
}

export interface UpdateStylePayload {
  brandId: string;
  styleName: string;
  style: {
    Name: string;
    Config: {
      Light: V3Style;
      Dark: V3Style;
    };
  };
}

export interface DeleteStylePayload {
  brandId: string;
  styleName: string;
}

const defaultLoadingSideEffects = {
  isLoading: false,
  isDefaultLoading: true,
  isUpdating: false,
  isDeleting: false,
  isAdding: false,
  isSuccess: false,
  isError: false,
  errorMessage: null
};
const defaultSuccessSideEffects = {
  isLoading: false,
  isDefaultLoading: false,
  isUpdating: false,
  isDeleting: false,
  isAdding: false,
  isSuccess: true,
  isError: false,
  errorMessage: null
};
const defaultErrorSideEffects = {
  isLoading: false,
  isDefaultLoading: false,
  isUpdating: false,
  isDeleting: false,
  isAdding: false,
  isSuccess: false,
  isError: true,
  errorMessage: null
};

// --- Initial State ---
const initialBrandsState: BrandsState = {
  Data: [],
  isLoading: false,
  isUpdating: false,
  isDeleting: false,
  isAdding: false,
  isSuccess: false,
  isError: false,
  errorMessage: null,
  errorCode: null
};

const initialCurrentBrandState: CurrentBrandState = {
  Data: null,
  isLoading: false,
  isContentUpdating: false,
  isDefaultLoading: false,
  isUpdating: false,
  isSuccess: false,
  isError: false,
  errorMessage: null,
  isAdding: false,
  isDeleting: false
};

// --- Brands Slice ---
export const BrandsSlice = createSlice({
  name: "hostedPageBrands",
  initialState: initialBrandsState,
  reducers: {
    getBrandsRequest(state) {
      Object.assign(state, loadingSideEffects);
    },
    getBrandsSuccess(state, action: PayloadAction<Brand[]>) {
      Object.assign(state, successSideEffects);
      const sortedData = [...action.payload].sort((a, b) => new Date(b.CreatedDate).getTime() - new Date(a.CreatedDate).getTime());
      state.Data = sortedData;
    },
    getBrandsFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects);
      state.errorMessage = action.payload;
      toastGetError(action.payload);
    },

    addBrandRequest(state, action: PayloadAction<AddBrandPayload>) {
      Object.assign(state, addingSideEffects, getRequestTrackingState(action));
    },
    addBrandSuccess(state, action: PayloadAction<Brand>) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      state.Data.unshift(action.payload);
      Toastify("success", "Template added successfully");
    },
    addBrandFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.errorMessage = action.payload;
      toastGetError(action.payload);
    },
    addBrandFromTemplateRequest(state, action: PayloadAction<{ templateName: string; brandName: string }>) {
      Object.assign(state, addingSideEffects, getRequestTrackingState(action));
    },
    addBrandFromTemplateSuccess(state, action: PayloadAction<Brand>) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      state.Data.unshift(action.payload);
      Toastify("success", "Template added successfully");
    },
    addBrandFromTemplateFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.errorMessage = action.payload;
      toastGetError(action.payload);
    },
    addBrandFromAIRequest(
      state,
      action: PayloadAction<{ brandName: string; imageFile?: string; url?: string; message?: string; selectedComponents?: string[]; primaryColor?: string; font?: string }>
    ) {
      Object.assign(state, addingSideEffects, getRequestTrackingState(action));
      state.errorCode = null;
    },
    addBrandFromAISuccess(state, action: PayloadAction<Brand>) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      state.Data.unshift(action.payload);
      Toastify("success", "Template added successfully");
    },
    addBrandFromAIFailure(state, action: PayloadAction<{ error: string; errorCode?: number }>) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.errorMessage = action.payload.error;
      state.errorCode = action.payload.errorCode ?? null;
      // Toast is handled by the component based on errorCode
    },

    updateBrandRequest(state, action: PayloadAction<UpdateBrandPayload>) {
      Object.assign(state, updatingSideEffects, getRequestTrackingState(action));
    },
    updateBrandSuccess(state, action: PayloadAction<Brand>) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      const idx = state.Data.findIndex((b) => b.id === action.payload.id);
      if (idx !== -1) {
        state.Data[idx] = action.payload;
        Toastify("success", "Template updated successfully");
      } else {
        toastGetError("Template not found");
      }
    },
    updateBrandFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.errorMessage = action.payload;
      toastGetError(action.payload);
    },

    deleteBrandRequest(state, action: PayloadAction<string>) {
      Object.assign(state, deletingSideEffects, getRequestTrackingState(action));
    },
    deleteBrandSuccess(state, action: PayloadAction<string>) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      state.Data = state.Data.filter((b) => b.id !== action.payload);
      Toastify("success", "Template deleted successfully");
    },
    deleteBrandFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.errorMessage = action.payload;
      toastGetError(action.payload);
    },

    // Set Default Brand
    setDefaultBrandRequest(state, action: PayloadAction<string>) {
      Object.assign(state, updatingSideEffects, getRequestTrackingState(action));
    },
    setDefaultBrandSuccess(state, action: PayloadAction<string>) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      // Mark only the selected brand as default, others as false
      state.Data = state.Data.map((brand) => ({
        ...brand,
        IsDefault: brand.id === action.payload
      }));
      Toastify("success", "Template deployed successfully");
    },
    setDefaultBrandFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.errorMessage = action.payload;
      toastGetError(action.payload);
    }
  }
});

// --- Current Brand Slice ---
export const CurrentBrandSlice = createSlice({
  name: "currentBrand",
  initialState: initialCurrentBrandState,
  reducers: {
    getCurrentBrandRequest(state, _: PayloadAction<string>) {
      Object.assign(state, loadingSideEffects);
    },
    getCurrentBrandSuccess(state, action: PayloadAction<Brand>) {
      Object.assign(state, successSideEffects);
      state.Data = action.payload;
    },
    getCurrentBrandFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects);
      state.errorMessage = action.payload;
    },

    updateCurrentBrandRequest(state, action: PayloadAction<UpdateBrandPayload>) {
      Object.assign(state, updatingSideEffects, getRequestTrackingState(action));
    },
    updateCurrentBrandSuccess(state, action: PayloadAction<Brand>) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      state.Data = action.payload;
      Toastify("success", "Template in current updated successfully");
    },
    updateCurrentBrandFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));

      state.errorMessage = action.payload;
      toastGetError(action.payload);
    },

    // Styles
    addStyleRequest(state, action: PayloadAction<AddStylePayload>) {
      Object.assign(state, addingSideEffects, getRequestTrackingState(action));
    },
    addStyleSuccess(state, action: PayloadAction<BrandStyle>) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      if (state.Data) {
        state.Data.Styles = [...(state.Data.Styles || []), action.payload];
      }
      Toastify("success", "Style added successfully");
    },
    addStyleFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.errorMessage = action.payload;
      toastGetError(action.payload);
    },

    updateStyleRequest(state, action: PayloadAction<UpdateStylePayload>) {
      Object.assign(state, updatingSideEffects, getRequestTrackingState(action));
    },
    updateStyleSuccess(state, action: PayloadAction<BrandStyle & { showToast?: boolean }>) {
      action.payload.showToast !== false && Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      if (state.Data) {
        const idx = state.Data.Styles.findIndex((s) => s.Name === action.payload.Name);
        if (idx !== -1) {
          state.Data.Styles[idx] = action.payload;
          if (action.payload.showToast !== false) {
            Toastify("success", "Style updated successfully");
          }
        }
      }
    },
    updateStyleFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.errorMessage = action.payload;
      toastGetError(action.payload);
    },

    deleteStyleRequest(state, action: PayloadAction<DeleteStylePayload>) {
      Object.assign(state, deletingSideEffects, getRequestTrackingState(action));
    },
    deleteStyleSuccess(state, action: PayloadAction<string>) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      if (state.Data) {
        state.Data.Styles = state.Data.Styles.filter((s) => s.Name !== action.payload);
      }
      Toastify("success", "Style deleted successfully");
    },
    deleteStyleFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.errorMessage = action.payload;
      toastGetError(action.payload);
    },

    // Set Default Style
    setDefaultStyleRequest(state, action: PayloadAction<{ brandId: string; styleName: string }>) {
      Object.assign(state, defaultLoadingSideEffects, getRequestTrackingState(action));
    },
    setDefaultStyleSuccess(state, action: PayloadAction<string>) {
      Object.assign(state, defaultSuccessSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      // action.payload is styleName
      if (state.Data) {
        state.Data.Styles = state.Data.Styles.map((style) => ({
          ...style,
          IsDefault: style.Name === action.payload
        }));
      }
      Toastify("success", "Style set as default");
    },
    setDefaultStyleFailure(state, action: PayloadAction<string>) {
      Object.assign(state, defaultErrorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.errorMessage = action.payload;
      toastGetError(action.payload);
    },
    addTranslationRequest(state, action: PayloadAction<{ brandId: string; locale: string }>) {
      Object.assign(state, updatingSideEffects, getRequestTrackingState(action));
    },
    addTranslationSuccess(state, action: PayloadAction<BrandI18n>) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      if (state.Data) {
        const existingLocale = state.Data.I18n.find((lang) => lang.Locale === action.payload.Locale);
        if (existingLocale) {
          existingLocale.Translations = {
            ...existingLocale.Translations,
            ...action.payload.Translations
          };
        } else {
          state.Data.I18n.push(action.payload);
        }
        Toastify("success", "Translation added successfully");
      }
    },
    addTranslationFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.errorMessage = action.payload;
      toastGetError(action.payload);
    },
    updateTranslationRequest(state, action: PayloadAction<{ brandId: string; locale: string; component: string; content: Record<string, any> }>) {
      Object.assign(state, updatingSideEffects, getRequestTrackingState(action));
    },
    updateTranslationSuccess(state, action: PayloadAction<BrandI18n & { showToast?: boolean }>) {
      action.payload.showToast !== false && Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      if (state.Data) {
        state.Data.I18n = state.Data.I18n.map((lang) => {
          if (lang.Locale === action.payload.Locale) {
            return {
              ...lang,
              Translations: {
                ...lang.Translations,
                ...action.payload.Translations
              }
            };
          }
          return lang;
        });
        if (action.payload.showToast !== false) {
          Toastify("success", "Translations updated successfully");
        }
      }
    },
    updateTranslationFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.errorMessage = action.payload;
      toastGetError(action.payload);
    },
    removeTranslationRequest(state, action: PayloadAction<{ brandId: string; locale: string }>) {
      Object.assign(state, updatingSideEffects, getRequestTrackingState(action));
    },
    removeTranslationSuccess(state, action: PayloadAction<{ brandId: string; locale: string }>) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      if (state.Data) {
        state.Data.I18n = state.Data.I18n.filter((lang) => lang.Locale !== action.payload.locale);
        Toastify("success", "Translation removed successfully");
      }
    },
    removeTranslationFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.errorMessage = action.payload;
      toastGetError(action.payload);
    },
    updateAllBrandContentRequest(state, action: PayloadAction<{ brandId: string; stylePayload: any | null; contentPayload: Record<string, any> | null }>) {
      Object.assign(state, contentUpdatingSideEffects, getRequestTrackingState(action));
    },
    updateAllBrandContentSuccess(state, _: PayloadAction<{ success: boolean }>) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      Toastify("success", "Template content updated successfully");
    },
    updateAllBrandContentFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.errorMessage = action.payload;
      toastGetError(action.payload);
    }
  },
  extraReducers: (builder) => {
    // Handle any additional actions here if needed
    builder.addCase(BrandsSlice.actions.setDefaultBrandRequest, (state, action: PayloadAction<string>) => {
      Object.assign(state, defaultLoadingSideEffects, getRequestTrackingState(action));
    });
    builder.addCase(BrandsSlice.actions.setDefaultBrandSuccess, (state, action: PayloadAction<string>) => {
      Object.assign(state, defaultSuccessSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      state.Data = state.Data ? { ...state.Data, IsDefault: state.Data.id === action.payload } : null;
    });
    builder.addCase(BrandsSlice.actions.setDefaultBrandFailure, (state, action: PayloadAction<string>) => {
      Object.assign(state, defaultErrorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.errorMessage = action.payload;
    });
  }
});

// --- Actions ---
export const {
  getBrandsRequest,
  getBrandsSuccess,
  getBrandsFailure,
  addBrandRequest,
  addBrandSuccess,
  addBrandFailure,
  addBrandFromTemplateRequest,
  addBrandFromTemplateSuccess,
  addBrandFromTemplateFailure,

  addBrandFromAIRequest,
  addBrandFromAISuccess,
  addBrandFromAIFailure,

  updateBrandRequest,
  updateBrandSuccess,
  updateBrandFailure,
  deleteBrandRequest,
  deleteBrandSuccess,
  deleteBrandFailure,
  setDefaultBrandRequest,
  setDefaultBrandSuccess,
  setDefaultBrandFailure
} = BrandsSlice.actions;

export const {
  getCurrentBrandRequest,
  getCurrentBrandSuccess,
  getCurrentBrandFailure,
  updateCurrentBrandRequest,
  updateCurrentBrandSuccess,
  updateCurrentBrandFailure,
  addTranslationRequest,
  addTranslationSuccess,
  addTranslationFailure,
  updateTranslationRequest,
  updateTranslationSuccess,
  updateTranslationFailure,
  removeTranslationRequest,
  removeTranslationSuccess,
  removeTranslationFailure,
  addStyleRequest,
  addStyleSuccess,
  addStyleFailure,
  updateStyleRequest,
  updateStyleSuccess,
  updateStyleFailure,
  deleteStyleRequest,
  deleteStyleSuccess,
  deleteStyleFailure,
  setDefaultStyleRequest,
  setDefaultStyleSuccess,
  setDefaultStyleFailure,
  updateAllBrandContentRequest,
  updateAllBrandContentSuccess,
  updateAllBrandContentFailure
} = CurrentBrandSlice.actions;

export default BrandsSlice.reducer;
